import { PrimaryButtonProps, SecondaryButtonProps } from '@workday/canvas-kit-react/button';
import { createComponent } from '@workday/canvas-kit-react/common';
import { createModifiers, createStyles } from '@workday/canvas-kit-styling';
import { LocationDescriptor } from 'history';
import * as React from 'react';
import { Canvas } from '../Canvas';
import DevsiteLink from './DevsiteLink';

interface LinkButtonProps {
  to: LocationDescriptor;
  replace?: boolean;
  innerRef?: (node: HTMLAnchorElement | null) => void;
  target?: string;
  rel?: string;
  grow?: boolean;
  hardLink?: boolean;
  download?: boolean | string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  disabled?: boolean;
  className?: string;
}

const linkStyles = createStyles({
  textDecoration: 'none !important',
  // No `pointerEvents: 'none'` here: navigation is already blocked via `handleDisabledLinkClick`
  // below, and disabling pointer events would also block hover, preventing a tooltip on this
  // element (e.g. explaining *why* it's disabled) from ever showing.
  '&.disabled': {
    cursor: 'default',
  },
});

const growModifiers = createModifiers({
  grow: {
    true: createStyles({
      width: '100%',
      maxWidth: '100%',
    }),
  },
});

/** Prevents navigation when link is disabled. */
function handleDisabledLinkClick(e: React.MouseEvent): void {
  e.preventDefault();
}

const LinkForButton: React.FC<LinkButtonProps> = createComponent('a')({
  displayName: 'LinkForButton',
  Component: ({ to, replace, hardLink, download, children, grow, className, disabled, ...props }) => {
    // disabled is intentionally not forwarded to <a>; parent LinkButton/SecondaryLinkButton handle it via class and aria-disabled.
    const { as: _as, ...linkProps } = props as typeof props & { as?: React.ElementType };
    const linkClassName = [linkStyles, growModifiers({ grow: !!grow }), className].filter(Boolean).join(' ');
    return hardLink || download ? (
      <a href={to.toString()} download={download} className={linkClassName} {...linkProps}>
        {children}
      </a>
    ) : (
      <DevsiteLink
        as='a'
        url={to.toString()}
        replace={replace}
        className={linkClassName}
        {...(linkProps as React.ComponentProps<typeof DevsiteLink>)}
      >
        {children}
      </DevsiteLink>
    );
  },
});

const LinkButton: React.FC<LinkButtonProps & PrimaryButtonProps> = createComponent(LinkForButton)({
  displayName: 'LinkButton',
  Component: (props, ref, Element) => {
    const { disabled, className, onClick, ...rest } = props;
    return (
      <Canvas.PrimaryButton
        ref={ref}
        as={Element}
        {...rest}
        className={[className, disabled && 'disabled'].filter(Boolean).join(' ') || undefined}
        aria-disabled={disabled ? true : undefined}
        onClick={disabled ? handleDisabledLinkClick : onClick}
      />
    );
  },
});

export const SecondaryLinkButton: React.FC<LinkButtonProps & SecondaryButtonProps> = createComponent(LinkForButton)({
  displayName: 'SecondaryLinkButton',
  Component: (props, ref, Element) => {
    const { disabled, className, onClick, ...rest } = props;
    return (
      <Canvas.SecondaryButton
        ref={ref}
        as={Element}
        {...rest}
        className={[className, disabled && 'disabled'].filter(Boolean).join(' ') || undefined}
        aria-disabled={disabled ? true : undefined}
        onClick={disabled ? handleDisabledLinkClick : onClick}
      />
    );
  },
});

export default LinkButton;
